import { useEffect, useState, useContext } from 'react';
import Head from 'next/head';
import classNames from 'classnames';
import Iframe from 'react-iframe';
import ReactPannellum from 'react-pannellum';

import PageContext from '@contexts/PageContext';
import styles from './Viewer360.module.scss';

const isValidUrl = (str) => {
  var urlPattern = new RegExp('^(https?:\\/\\/)?'+ // validate protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))'+ // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?'+ // validate query string
      '(\\#[-a-z\\d_]*)?$','i'); // validate fragment locator
  return !!urlPattern.test(str);
};

const config = {
  autoLoad: true,
  width: '100%',
  height: '100%',
  background: '#ffffff',
  showFullscreenCtrl: true
};

const Viewer360 = ({ Id, DarkMode, subMenuOptions }) => {
  const pageData = useContext(PageContext);
  const [activeTab, setActiveTab] = useState(null);
  const [galleryContent, setGalleryContent] = useState([]);
  const [isSirvSpinBg, setIsSirvSpinBg] = useState(false); // wrapper styling

  useEffect(() => {
    if (subMenuOptions?.length > 0) {
      const tab = subMenuOptions[0];
      setActiveTab({...tab});
      loadUrls(tab.id);
    }
  }, []);

  const handleTabClick = (e) => {
    e.preventDefault();
    const tab = subMenuOptions?.find((menu) => menu.id === e.target.value);
    if (tab) {
      setActiveTab({ ...tab });
      loadUrls(e.target.value);
    }
  };

  const loadUrls = (id) => {
    const content =
      pageData.ProductGallery.find((x) => x.Description == '360')?.GalleryItems.find(
        (item) => item.Id == id
      )?.ProductGalleryContent;

    const items = Array.isArray(content) ? content : content ? [content] : [];

    const deduped = items.filter(
      (it, i, arr) =>
        arr.findIndex((x) => (x.FileNameUrl || '') === (it.FileNameUrl || '')) === i
    );

    setGalleryContent(deduped);

    setIsSirvSpinBg(
      deduped.some(
        (it) =>
          (it.FileNameUrl || '').includes('sirv.com') &&
          (it.FileNameUrl || '').includes('.spin')
      )
    );
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (typeof window !== 'undefined' && window.Sirv && typeof window.Sirv.refresh === 'function') {
        window.Sirv.refresh();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [galleryContent]);

  const videoScripts = Array.from(
    new Set(
      galleryContent
        .map((it) => it?.VideoUrl)
        .filter((src) => isValidUrl(src))
    )
  );

  return (
    <>
      <Head>
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/pannellum@2.5.4/build/pannellum.css"
        />
        <link rel="stylesheet" href="/styles/hotspots.css" />
        <script src="https://cdn.jsdelivr.net/npm/pannellum@2.5.4/build/pannellum.js"></script>
        <script src="https://scripts.sirv.com/sirvjs/v3/sirv.js"></script>
        {videoScripts.map((src) => (
          <script key={src} src={src}></script>
        ))}
        <script src="/scripts/jquery-3.5.1.min.js" />
        <script src="/scripts/hotspots.js"></script>
      </Head>

      <div
        className={classNames(
          activeTab?.label?.includes('Exterior') ? styles.Wrapper : styles.NoSpinBg,
          DarkMode ? styles.DarkMode : null,
          isSirvSpinBg ? styles.IsSpin : null
        )}
      >
        {galleryContent.map((item, idx) => {
          const fn = item?.FileNameUrl || '';
          const vid = isValidUrl(item?.VideoUrl) ? item.VideoUrl : null;

          const isSpin =
            fn.includes('sirv.com') && fn.toLowerCase().includes('.spin');
          const isSirvImg =
            fn.includes('sirv.com') && /\.(png|jpe?g)(\?|#|$)/i.test(fn);

          const pannellumId = `pnl-${activeTab?.id}-${idx}`;

          return (
            <div key={`${activeTab?.id}-${item?.Language ?? 'x'}-${idx}`}>
              {/* Sirv .spin */}
              {!vid && isSpin && (
                <div
                  className="Sirv"
                  data-options="thumbnails.type: auto; arrows: false;"
                >
                  <div data-src={fn}></div>
                </div>
              )}

              {!vid && isSirvImg && (
                <ReactPannellum
                  id={pannellumId}
                  sceneId={`${pannellumId}-scene`}
                  imageSource={fn}
                  config={config}
                  width="100%"
                  className={styles.PannelContainer}
                />
              )}

              {!vid && !isSpin && !isSirvImg && fn && (
                <Iframe
                  url={fn} // correct prop for react-iframe
                  position="absolute"
                  width="100%"
                  height="100%"
                  id={`${Id || 'viewer'}-${idx}`} // unique
                />
              )}

              {vid && (
                <div
                  className="Sirv"
                  data-options="thumbnails.type: auto; arrows: false;"
                >
                  <div
                    data-type="panorama"
                    data-id={`pannellum-panorama-${idx}`}
                    data-thumbnail-html="<div>Interior</div>"
                  >
                    <div id={`panorama-${idx}`}></div>
                    <div className="custom-popup" id={`custom-popup-${idx}`}>
                      <div className="close-popup">
                        <span className="close-popup-button">×</span>
                      </div>
                      <div className="popup-content"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={classNames(styles.IconBox, DarkMode ? styles.DarkMode : null)}>
        {subMenuOptions?.map((item) => (
          <button
            key={item.id}
            value={item.id}
            className={`${styles.Button} ${activeTab?.id === item.id ? styles.active : ''}`}
            onClick={handleTabClick}
          >
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
};

export default Viewer360;
